import { useAppContext } from '../../../../Services/AppContext';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../Stepper/NavigationStepper';
import HealthPlanUserInformation from '../../../../Modules/HealthPlan/Components/HealthPlanUserInformation';
import HealthPlanUserInitQuestionnaire from '../../../../Modules/HealthPlan/Components/HealthPlanUserInitQuestionnaire';
import HealthPlanWrapperContainer from '../../HealthPlanWrapper/HealthPlanWrapperContainer';
import HealthPlanModal from '../../../../Modules/HealthPlan/Components/HealthPlanModal';
import HealthPlanWrapperCollapse from '../../HealthPlanWrapper/HealthPlanWrapperCollapse';
import HealthPlanPricing from '../../Create/HealthPlanPricing';
import React, { useMemo } from 'react';
import { HealthPlanUser, Meta } from '../../../../graphql/graphql';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import useScope from '../../../../Modules/Scope/Hooks/useScope';

interface Props {
	patientId: string;
	handleUserInformationSuccess: (
		params: NavigationStepperComponentParams,
	) => void;
	handleInitQuestionnaireSuccess: (
		data: { questionnaireResultId: string },
		params: NavigationStepperComponentParams,
	) => void;
	handleSetupSuccess: (params: NavigationStepperComponentParams) => void;
	handlePricingSuccess: (
		params: NavigationStepperComponentParams,
		promoCode?: string,
	) => void;
	planIsForFree?: Meta | null;
	healthPlanUser: HealthPlanUser;
}

export const useMeddiDiabetesCreateSteps = ({
	patientId,
	handleUserInformationSuccess,
	handleInitQuestionnaireSuccess,
	handleSetupSuccess,
	handlePricingSuccess,
	planIsForFree,
	healthPlanUser,
}: Props) => {
	const { globalTemplate } = useAppContext();
	const { isLatam } = useScope();

	const steps: NavigationStepperItemType[] = useMemo(() => {
		let tempSteps: NavigationStepperItemType[] = [
			{
				name: 'base.informationAboutPatient',
				key: 'morePatientsInfo',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanUserInformation
						patientId={patientId}
						onSuccess={() => handleUserInformationSuccess(params)}
						setIsLoading={params.setIsLoading}
					/>
				),
			},
			{
				name: 'base.entryQuestionnaireRevision',
				key: 'reviseEntryQuestionnaire',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanUserInitQuestionnaire
						patientId={patientId}
						onSuccess={data => handleInitQuestionnaireSuccess(data, params)}
						setIsLoading={params.setIsLoading}
					/>
				),
			},
			{
				name: 'diabetes.healthPlan.healthPlanOverview',
				key: 'overview',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanWrapperContainer
						patientId={patientId}
						onSuccess={() =>
							planIsForFree
								? handlePricingSuccess(params)
								: handleSetupSuccess(params)
						}
						modalComponent={HealthPlanModal}
						modalItems={13}
						modalItemPrefix='diabetes.infoModal.items'
						modalTitle={'diabetes.infoModal.title'}
						modalSubtitle='diabetes.infoModal.subtitle'
						modalDescription='diabetes.infoModal.text2'
						modalExtraText={['diabetes.infoModal.text3']}
						wrapperCollapseTitle={
							isLatam ? 'diabetes.healthPlan.wrapperCollapseTitle' : undefined
						}
						modalImages={[
							'https://cdn.meddi.com/assets/latam/alad.png',
							'https://cdn.meddi.com/assets/latam/sociedad_ecuatoriana_de_endocrinologia.png',
						]}
						wrapperCollapse={HealthPlanWrapperCollapse}
						setIsLoading={params.setIsLoading}
						healthPlanUser={healthPlanUser}
					/>
				),
				cantGoBack: true,
			},
		];

		if (!planIsForFree) {
			tempSteps.push({
				name: 'component.createPriceSetup.billing',
				key: 'priceSetup',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanPricing
						patientId={patientId}
						onSuccess={(promoCode?: string) =>
							handlePricingSuccess(params, promoCode)
						}
						setIsLoading={params.setIsLoading}
						healthPlanCode={HealthPlanCode.DIABETES}
					/>
				),
				cantGoBack: true,
			});
		}

		if (globalTemplate === 'default') {
			tempSteps = tempSteps.filter(step => {
				return !['morePatientsInfo'].includes(step.key);
			});
		}

		return tempSteps;
	}, [globalTemplate, planIsForFree]);

	return {
		steps,
		isLoading: false,
	};
};
